// "Scan to Pay" poster renderer for the Toolkit designer.
//
// Bold color-block design (inspired by organizer-made posters): big uppercase
// headline, optional phone frame, white QR card, SCAN TO PAY, app badges.
// Three print sizes with exact @page dimensions so Save-as-PDF comes out
// print-shop ready. Same preview-window + Print technique as
// lib/printer/system-print — dependency-free.

function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

/** Colors land inside a <style> block — only allow hex so a crafted value
 *  can't inject CSS. */
export function safeHexColor(value: string | null | undefined, fallback: string): string {
  const v = (value || '').trim()
  return /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v) ? v : fallback
}

/** Best-effort readable text color for a hex background. */
export function contrastText(hex: string): string {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6)
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#111111' : '#ffffff'
}

export type PosterSize = 'a4' | 'postcard' | 'business-card'

export const POSTER_SIZES: Record<
  PosterSize,
  { label: string; wMm: number; hMm: number; page: string }
> = {
  a4: { label: 'A4 poster', wMm: 210, hMm: 297, page: 'A4 portrait' },
  postcard: { label: 'Postcard 6×4"', wMm: 152.4, hMm: 101.6, page: '6in 4in' },
  'business-card': { label: 'Business card 3.5×2"', wMm: 88.9, hMm: 50.8, page: '3.5in 2in' },
}

export interface PosterConfig {
  size: PosterSize
  /** Background hex color. Text color is auto-contrast. */
  bg: string
  headline: string
  subline: string
  /** The link the QR encodes. */
  url: string
  /** Human-facing link text to PRINT (defaults to `url`). The QR can point at
   *  a tracked short link while the printed text stays the pretty page URL. */
  displayUrl?: string
  qrDataUrl: string
  eventName: string
  logoUrl?: string | null
  showPhoneFrame: boolean
  showLogo: boolean
  showBadges: boolean
  showUrl: boolean
}

// Compact inline SVGs so app badges render on any OS (no emoji/font glyphs).
const APPLE_SVG =
  '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-1-1.94.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.49.96 1.39 2.1 2.94 3.6 2.88 1.45-.06 1.99-.93 3.74-.93s2.24.93 3.77.9c1.56-.03 2.54-1.41 3.49-2.81 1.1-1.61 1.55-3.17 1.58-3.25-.04-.02-3.03-1.16-3.06-4.58zM14.16 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.74-.74.85-1.39 2.22-1.22 3.53 1.29.1 2.6-.65 3.41-1.61z"/></svg>'
const PLAY_SVG =
  '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M3 2.5v19l11-9.5L3 2.5zM15.3 10l3.4 2-3.4 2-2.6-2 2.6-2zM4.8 1.6l10 6.1-2.3 2L4.8 1.6zm0 20.8l7.7-8.1 2.3 2-10 6.1z"/></svg>'

interface Palette {
  bg: string
  tx: string
  /** Slightly lifted panel color for the phone screen. */
  screen: string
}

function buildPalette(bgInput: string): Palette {
  const bg = safeHexColor(bgInput, '#2563eb')
  const tx = contrastText(bg)
  const screen =
    tx === '#ffffff'
      ? 'color-mix(in srgb, ' + bg + ' 78%, white)'
      : 'color-mix(in srgb, ' + bg + ' 88%, black)'
  return { bg, tx, screen }
}

function badgesHtml(scale = 1): string {
  // Official-badge-style pills: small kicker line over the store name,
  // white pill, black mark — two lines, never wrapping.
  const pill =
    'display:inline-flex;align-items:center;gap:' + 2 * scale + 'mm;background:#ffffff;color:#111;' +
    'border-radius:999px;padding:' + 1.7 * scale + 'mm ' + 4 * scale + 'mm;white-space:nowrap;'
  const col = 'display:flex;flex-direction:column;text-align:left;line-height:1.12;'
  const kicker = `font-size:${6.5 * scale}px;font-weight:600;letter-spacing:.2px;`
  const store = `font-size:${10.5 * scale}px;font-weight:800;`
  return `
    <div style="display:flex;gap:${3 * scale}mm;">
      <span style="${pill}">${APPLE_SVG}<span style="${col}"><span style="${kicker}">Download on the</span><span style="${store}">App Store</span></span></span>
      <span style="${pill}">${PLAY_SVG}<span style="${col}"><span style="${kicker}">GET IT ON</span><span style="${store}">Google Play</span></span></span>
    </div>`
}

/** Printed link text: displayUrl (or url) without scheme or trailing slash. */
function displayText(c: PosterConfig): string {
  return escapeHtml((c.displayUrl || c.url).replace(/^https?:\/\//i, '').replace(/\/$/, ''))
}

function qrCardHtml(qrDataUrl: string, qrMm: number, radiusMm: number, padMm: number): string {
  return `
    <div style="background:#fff;border-radius:${radiusMm}mm;padding:${padMm}mm;display:inline-block;line-height:0;">
      <img src="${qrDataUrl}" alt="QR code" style="width:${qrMm}mm;height:${qrMm}mm;display:block;" />
    </div>`
}

function a4Body(c: PosterConfig, p: Palette): string {
  // Proportions follow the reference poster: headline block ~top 15%, then a
  // WIDE phone (≈75% of sheet) that runs off the bottom edge, with the QR,
  // SCAN TO PAY, badges and URL living INSIDE the screen.
  // Logo sits at the TOP of the screen (right under the notch); the QR group
  // is centered in the remaining space (padding-bottom offsets the URL zone).
  const logo = c.showLogo && c.logoUrl
    ? `<img src="${escapeHtml(c.logoUrl)}" alt="" style="flex:0 0 auto;max-height:26mm;max-width:76mm;object-fit:contain;margin:8mm auto 0;" />`
    : ''
  const inner = `
    <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding-bottom:34mm;">
      ${qrCardHtml(c.qrDataUrl, 72, 6.5, 5.5)}
      <div style="font-size:44px;font-weight:900;letter-spacing:1.5px;margin-top:10mm;">SCAN TO PAY</div>
      ${c.showBadges ? `<div style="margin-top:7mm;display:flex;justify-content:center;">${badgesHtml()}</div>` : ''}
    </div>
  `
  const centre = c.showPhoneFrame
    ? `
      <div style="margin-top:9mm;width:158mm;flex:1;border:4.5mm solid #111;border-bottom:none;border-radius:18mm 18mm 0 0;background:${p.screen};padding:7mm 10mm 0;display:flex;flex-direction:column;align-items:center;text-align:center;">
        <div style="flex:0 0 auto;width:30mm;height:7mm;background:#111;border-radius:999px;margin:0 auto;"></div>
        ${logo}
        ${inner}
      </div>`
    : `<div style="margin-top:10mm;flex:1;display:flex;flex-direction:column;align-items:center;text-align:center;">${logo}${inner}</div>`

  return `
    <div class="sheet" style="flex-direction:column;align-items:center;text-align:center;padding:14mm 14mm 0;">
      <div style="font-size:54px;font-weight:900;letter-spacing:1px;line-height:1.05;max-width:182mm;">${escapeHtml(c.headline)}</div>
      <div style="font-size:18px;font-weight:800;letter-spacing:1px;margin-top:3.5mm;opacity:.95;">${escapeHtml(c.subline)}</div>
      ${centre}
      ${c.showUrl ? `<div style="position:absolute;bottom:9mm;left:0;right:0;text-align:center;font-size:16px;font-weight:800;letter-spacing:1.4px;text-transform:uppercase;">${displayText(c)}</div>` : ''}
    </div>`
}

function postcardBody(c: PosterConfig): string {
  // object-position + align-self keep the logo flush LEFT (a bare flex child
  // stretches and object-fit would otherwise center the image in its box).
  const logo = c.showLogo && c.logoUrl
    ? `<img src="${escapeHtml(c.logoUrl)}" alt="" style="max-height:19mm;max-width:54mm;object-fit:contain;object-position:left;align-self:flex-start;margin-bottom:3.5mm;" />`
    : ''
  return `
    <div class="sheet" style="flex-direction:row;align-items:center;gap:8mm;padding:9mm 9.5mm;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;">
        ${logo}
        <div style="font-size:22px;font-weight:900;letter-spacing:.3px;line-height:1.06;white-space:nowrap;">${escapeHtml(c.headline)}</div>
        <div style="font-size:11px;font-weight:800;letter-spacing:.7px;margin-top:2.5mm;opacity:.95;">${escapeHtml(c.subline)}</div>
        <div style="font-size:19px;font-weight:900;letter-spacing:1.1px;margin-top:5mm;">SCAN TO PAY</div>
        ${c.showBadges ? `<div style="margin-top:3.5mm;display:flex;">${badgesHtml(0.85)}</div>` : ''}
        ${c.showUrl ? `<div style="font-size:9px;font-weight:800;letter-spacing:.7px;text-transform:uppercase;margin-top:3.5mm;opacity:.9;">${displayText(c)}</div>` : ''}
      </div>
      <div style="flex:0 0 auto;">${qrCardHtml(c.qrDataUrl, 52, 4, 4)}</div>
    </div>`
}

function businessCardBody(c: PosterConfig): string {
  // Logo replaces the event-name line when present + enabled; toggling the
  // logo off falls back to the name.
  const identity = c.showLogo && c.logoUrl
    ? `<img src="${escapeHtml(c.logoUrl)}" alt="" style="max-height:13mm;max-width:26mm;object-fit:contain;object-position:left;align-self:flex-start;" />`
    : `<div style="font-size:10.5px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;opacity:.95;">${escapeHtml(c.eventName)}</div>`
  return `
    <div class="sheet" style="flex-direction:row;align-items:center;gap:4mm;padding:5mm 5.5mm;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;">
        ${identity}
        <div style="font-size:11px;font-weight:900;letter-spacing:.5px;margin-top:1.6mm;white-space:nowrap;">SETUP YOUR WALLET</div>
        ${c.showUrl ? `<div style="font-size:6.5px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;margin-top:1.8mm;opacity:.9;white-space:nowrap;">${displayText(c)}</div>` : ''}
      </div>
      <div style="flex:0 0 auto;">${qrCardHtml(c.qrDataUrl, 34, 2.5, 2.2)}</div>
    </div>`
}

/** Full HTML document for the poster. `preview` strips the toolbar/margins so
 *  it can live inside a scaled iframe in the designer. */
export function buildPosterHtml(c: PosterConfig, opts: { preview?: boolean } = {}): string {
  const size = POSTER_SIZES[c.size]
  const p = buildPalette(c.bg)
  const body =
    c.size === 'a4' ? a4Body(c, p) : c.size === 'postcard' ? postcardBody(c) : businessCardBody(c)

  const toolbar = opts.preview
    ? ''
    : `<div class="toolbar"><button class="print-btn" onclick="window.print()">Print / Save as PDF</button></div>`

  return `
    <html>
      <head>
        <title>${escapeHtml(c.eventName)} — ${size.label}</title>
        <style>
          * { box-sizing: border-box; margin: 0; }
          html, body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            background: ${opts.preview ? 'transparent' : '#eceef1'};
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          .toolbar { display:flex; justify-content:flex-end; padding:12px 16px; background:#fff; border-bottom:1px solid #e5e5e5; }
          .print-btn { background:${p.bg}; color:${p.tx}; border:none; border-radius:8px; padding:9px 16px; font-size:14px; font-weight:600; cursor:pointer; }
          .sheet {
            position: relative; overflow: hidden; display: flex;
            width: ${size.wMm}mm; height: ${size.hMm}mm;
            margin: ${opts.preview ? '0' : '24px auto'};
            background: ${p.bg}; color: ${p.tx};
            ${opts.preview ? '' : 'box-shadow: 0 4px 24px rgba(0,0,0,.16); border-radius: 2mm;'}
          }
          @media print {
            @page { size: ${size.page}; margin: 0; }
            body { background: #fff; }
            .toolbar { display: none; }
            .sheet { margin: 0; box-shadow: none; border-radius: 0; }
          }
        </style>
      </head>
      <body>${toolbar}${body}</body>
    </html>`
}

/**
 * Renders the poster into `targetWindow` when provided, else opens one.
 *
 * IMPORTANT: browsers only honour window.open during the SYNCHRONOUS part of a
 * user gesture — open the window in the click handler and pass it in when any
 * async work (QR generation) precedes rendering.
 */
export function openPosterPreview(c: PosterConfig, targetWindow?: Window | null): void {
  if (typeof window === 'undefined') return
  const previewWindow = targetWindow ?? window.open('', '_blank')
  if (!previewWindow) {
    throw new Error('Unable to open poster preview — allow pop-ups for this site.')
  }
  previewWindow.document.open()
  previewWindow.document.write(buildPosterHtml(c))
  previewWindow.document.close()
  previewWindow.focus()
}
